/**
 * Ticket Data Transformers
 * Functions to transform API data to component format
 */

import * as indexedDB from '../../../utils/indexedDB';
import * as tokenEncryption from '../../../utils/tokenEncryption';

/**
 * Get auth token from secure storage with fallback
 */
export const getTokenFromStorage = async (key) => {
  try {
    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem(key);
      if (encryptedToken) {
        const decryptedToken = await tokenEncryption.decryptToken(encryptedToken);
        if (decryptedToken) {
          return decryptedToken;
        }
      }
    }

    // Fallback to localStorage if IndexedDB fails
    const localToken = localStorage.getItem(key);
    if (localToken) {
      return localToken;
    }

    return null;
  } catch (error) {
    console.error('Error retrieving token:', error);
    // Last resort: try localStorage directly
    return localStorage.getItem(key);
  }
};

/**
 * Transform API ticket data to component format
 */
export const transformTicketData = (ticket) => {
  if (!ticket) {
    console.error('transformTicketData called with null/undefined ticket');
    return null;
  }

  // Handle user data - API returns 'name' field (from username)
  const createdByUser = ticket.createdByUser || ticket.created_by_user;
  const transformedCreatedByUser = createdByUser ? {
    id: createdByUser.id,
    email: createdByUser.email,
    name: createdByUser.name || createdByUser.username || 'Unknown',
    firstName: createdByUser.firstName || (createdByUser.name || createdByUser.username)?.split(' ')[0] || 'Unknown',
    lastName: createdByUser.lastName || (createdByUser.name || createdByUser.username)?.split(' ').slice(1).join(' ') || ''
  } : { firstName: 'Unknown', lastName: '', name: 'Unknown' };

  const supportUser = ticket.supportUser || ticket.support_user;
  const transformedSupportUser = supportUser ? {
    id: supportUser.id,
    email: supportUser.email,
    name: supportUser.name || supportUser.username || 'Unknown',
    firstName: supportUser.firstName || (supportUser.name || supportUser.username)?.split(' ')[0] || 'Unknown',
    lastName: supportUser.lastName || (supportUser.name || supportUser.username)?.split(' ').slice(1).join(' ') || ''
  } : null;

  // Handle location data - normalize to have both friendlyName and name
  // Check if location is an object (not just an ID number)
  const rawLocation = ticket.Location || ticket.location || ticket.locationModel;
  const isLocationObject = rawLocation && typeof rawLocation === 'object' && rawLocation !== null;
  const normalizedLocation = isLocationObject ? {
    ...rawLocation,
    id: rawLocation.id || rawLocation._id,
    // Don't use a fallback - let the table look it up from locations array if not present
    friendlyName: rawLocation.friendlyName || rawLocation.friendly_name || rawLocation.name || null,
    name: rawLocation.name || rawLocation.friendlyName || rawLocation.friendly_name || null
  } : null;

  // Handle locationId - check if location itself is just an ID (number or string)
  const locationIdFromLocation = typeof rawLocation === 'number' || typeof rawLocation === 'string'
    ? rawLocation
    : (rawLocation?.id || rawLocation?._id);

  const transformed = {
    ...ticket,
    id: ticket.id || ticket._id,
    title: ticket.title || ticket.description?.substring(0, 50),
    status: ticket.status || 'Open',
    urgency: ticket.urgency || 'MEDIUM',
    support_type: ticket.support_type || 'other',
    location: normalizedLocation,
    locationId: ticket.locationId || ticket.location_id || locationIdFromLocation,
    related_device_id: ticket.related_device_id || ticket.relatedDeviceId || ticket.deviceId || ticket.device_id,
    related_device_name: ticket.related_device_name || ticket.relatedDeviceName || (ticket.Device ? ticket.Device.name || ticket.Device.friendlyName : null),
    next_step: ticket.next_step || ticket.nextStep || '',
    support_user_id: ticket.support_user_id || ticket.supportUserId || ticket.supportUser?.id,
    createdByUser: transformedCreatedByUser,
    createdByCompany: ticket.createdByCompany || ticket.created_by_company,
    supportUser: transformedSupportUser,
    supportCompany: ticket.supportCompany || ticket.support_company,
    created_at: ticket.created_at || ticket.createdAt,
    updated_at: ticket.updated_at || ticket.updatedAt,
    createdAt: ticket.createdAt || ticket.created_at,
    updatedAt: ticket.updatedAt || ticket.updated_at,
    TicketAttachments: ticket.TicketAttachments || [],
    TicketMessages: ticket.TicketMessages || [],
    followers: ticket.followers || [],
    appliedSolutions: ticket.appliedSolutions || []
  };

  return transformed;
};

/**
 * Transform locations data for TreeSelect component
 */
export const transformLocationsForTreeSelect = (locations) => {
  if (!Array.isArray(locations)) return [];

  const buildLocationTree = (locations, parentId = null) => {
    return locations
      .filter((loc) => {
        const locParentId = loc.parentId || loc.parent_id || loc.parentLocation;
        if (parentId === null) {
          return !locParentId || locParentId === 0 || locParentId === '';
        }
        return locParentId === parentId;
      })
      .map((loc) => ({
        id: loc.id || loc._id || loc.location_UUID,
        _id: loc._id || loc.id || loc.location_UUID,
        name: loc.name || loc.friendlyName,
        friendlyName: loc.friendlyName || loc.name,
        locationType: loc.locationType || loc.location_type || 1,
        parentLocation: loc.parentLocation || loc.parent_id || loc.parentId,
        children: buildLocationTree(locations, loc.id || loc._id || loc.location_UUID)
      }));
  };

  // Check if data is already in tree format
  const hasChildren = locations.some((loc) => loc.children && Array.isArray(loc.children));

  if (hasChildren) {
    return locations.map((loc) => ({
      id: loc.id || loc._id || loc.location_UUID,
      _id: loc._id || loc.id || loc.location_UUID,
      name: loc.name || loc.friendlyName,
      friendlyName: loc.friendlyName || loc.name,
      locationType: loc.locationType || loc.location_type || 1,
      parentLocation: loc.parentLocation || loc.parent_id || loc.parentId,
      children: loc.children || []
    }));
  } else {
    const tree = buildLocationTree(locations);
    return tree.length > 0 ? tree : locations;
  }
};

/**
 * Check if a location is a site (locationType = 1)
 */
export const isLocationTypeSite = (loc) => {
  if (!loc) return false;
  const locType = loc.locationType
    || loc.location_type
    || loc.locationTypeModel?.id
    || loc.locationTypeModel?.value
    || loc.LocationType?.id
    || loc.locationTypeId;
  return locType === 1;
};

/**
 * Find the site (locationType = 1) for a location
 */
export const findSiteForLocation = (locationId, locations) => {
  if (!locationId || !locations || locations.length === 0) return null;

  const locationMap = new Map();
  locations.forEach(loc => {
    const id = loc.id || loc._id;
    if (id) {
      locationMap.set(id, loc);
      locationMap.set(String(id), loc);
      locationMap.set(Number(id), loc);
    }
    if (loc._id) {
      locationMap.set(loc._id, loc);
      locationMap.set(String(loc._id), loc);
    }
  });

  let currentLocation = locationMap.get(locationId)
    || locationMap.get(String(locationId))
    || locationMap.get(Number(locationId));

  if (!currentLocation) return null;

  if (isLocationTypeSite(currentLocation)) {
    return currentLocation;
  }

  let iterations = 0;
  const maxIterations = 100;
  let highestParent = currentLocation;

  while (iterations < maxIterations) {
    const parentId = currentLocation.parentId
      || currentLocation.parent_id
      || currentLocation.parentLocation
      || currentLocation.parent;

    if (!parentId) break;

    const parentLocation = locationMap.get(parentId)
      || locationMap.get(String(parentId))
      || locationMap.get(Number(parentId));

    if (!parentLocation) break;

    highestParent = parentLocation;

    if (isLocationTypeSite(parentLocation)) {
      return parentLocation;
    }

    currentLocation = parentLocation;
    iterations++;
  }

  return highestParent;
};

/**
 * Format date string to localized format
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Get relative time difference string
 */
export const getTimeDifference = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  } catch {
    return '';
  }
};

/**
 * Wildcard search helper function
 * Converts wildcard pattern (with *) to regex and tests against text
 */
export const wildcardMatch = (text, pattern) => {
  if (!text || !pattern) return false;
  const textStr = String(text);
  const patternStr = String(pattern);

  if (!patternStr.includes('*')) {
    return textStr.toLowerCase().includes(patternStr.toLowerCase());
  }

  const regexPattern = patternStr
    .toLowerCase()
    .split('*')
    .map(part => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
    .join('.*');

  const regex = new RegExp(`^${regexPattern}$`, 'i');
  return regex.test(textStr.toLowerCase());
};

/**
 * Check if search term matches any field in ticket
 */
export const matchesSearch = (ticket, searchTerm) => {
  if (!searchTerm) return true;

  const fields = [
    String(ticket.id),
    ticket.title,
    ticket.description,
    ticket.location?.friendlyName,
    ticket.location?.name,
    ticket.related_device_name,
    ticket.createdByUser?.name,
    ticket.createdByUser?.email,
    ticket.next_step
  ];

  return fields.some(field => field && wildcardMatch(field, searchTerm));
};
